import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Pagination,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import TuneIcon from '@mui/icons-material/Tune';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotifications,
  markAsRead,
  markAsUnread,
  markAllAsRead,
  deleteNotification,
} from '../services/notificationService';
import { formatDistanceToNow, format } from 'date-fns';
import { he } from 'date-fns/locale';

const PAGE_SIZE = 6;

const NotificationsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0); // 0=all, 1=unread
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  const isReadFilter = tab === 1 ? false : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'list', tab, page],
    queryFn: () => getNotifications({ page, pageSize: PAGE_SIZE, isRead: isReadFilter }),
    staleTime: 30000,
    keepPreviousData: true,
  });

  const markReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: (err) => setError(err?.response?.data?.error || 'שגיאה'),
  });

  const markUnreadMutation = useMutation({
    mutationFn: markAsUnread,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: (err) => setError(err?.response?.data?.error || 'שגיאה'),
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: (err) => setError(err?.response?.data?.error || 'שגיאה'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: (err) => setError(err?.response?.data?.error || 'שגיאה'),
  });

  const handleNotifClick = useCallback((notif) => {
    if (!notif.IsRead) {
      markReadMutation.mutate(notif.NotificationID);
    }
    if (notif.EntityType === 'client' && notif.ClientBusinessID) {
      navigate(`/client/${notif.ClientBusinessID}`);
    } else if (notif.EntityType === 'supplier' && notif.SupplierID) {
      navigate(`/supplier/${notif.SupplierID}`);
    }
  }, [markReadMutation, navigate]);

  const handleDelete = useCallback((e, notifId) => {
    e.stopPropagation();
    deleteMutation.mutate(notifId);
  }, [deleteMutation]);

  const notifications = data?.results || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          התראות
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TuneIcon />}
            onClick={() => navigate('/notification-rules')}
            sx={{ textTransform: 'none', direction: 'ltr' }}
          >
            ניהול כללים
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            sx={{ textTransform: 'none', direction: 'ltr' }}
          >
            סמן הכל כנקרא
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); setPage(1); }}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="הכל" />
          <Tab label="לא נקראו" />
        </Tabs>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {tab === 1 ? 'אין התראות שלא נקראו' : 'אין התראות'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notif) => (
              <ListItem
                key={notif.NotificationID}
                disablePadding
                secondaryAction={
                  <Tooltip title="מחק">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleDelete(e, notif.NotificationID)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemButton
                  onClick={() => handleNotifClick(notif)}
                  sx={{
                    py: 2,
                    px: 2.5,
                    backgroundColor: notif.IsRead ? 'transparent' : 'rgba(37, 99, 235, 0.04)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', pl: 4, direction: 'ltr' }}>
                    <Tooltip title={notif.IsRead ? 'סמן כלא נקרא' : 'סמן כנקרא'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (notif.IsRead) {
                            markUnreadMutation.mutate(notif.NotificationID);
                          } else {
                            markReadMutation.mutate(notif.NotificationID);
                          }
                        }}
                        sx={{ mt: 0.2, flexShrink: 0 }}
                      >
                        {notif.IsRead ? (
                          <DraftsOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                        ) : (
                          <MailOutlineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ flexShrink: 0, mt: 0.3 }}>
                      {notif.EntityType === 'client' ? (
                        <BusinessIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      ) : (
                        <LocalShippingIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                      )}
                    </Box>
                    <Tooltip title={`${notif.Title}\n${notif.Message}`} placement="bottom" enterDelay={400}>
                    <Box sx={{ flex: 1, minWidth: 0, direction: 'rtl', textAlign: 'right' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: notif.IsRead ? 'normal' : 'bold', mb: 0.3 }}
                      >
                        {notif.Title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {notif.Message}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start' }}>
                        <Chip
                          label={notif.EntityType === 'client' ? 'לקוח' : 'ספק'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr' }}>
                          {format(new Date(notif.CreatedAt), 'dd/MM/yyyy HH:mm')}
                          {' · '}
                          {formatDistanceToNow(new Date(notif.CreatedAt), { addSuffix: true, locale: he })}
                        </Typography>
                      </Box>
                    </Box>
                    </Tooltip>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default NotificationsPage;
